import { useLocation, useNavigate } from 'react-router-dom'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DocumentFilteringOption } from '@/types/documents'
import useColumnFilters from '@/hooks/useColumnFilters'
import { getBaseGrid } from '@/utils/field'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedGridsContext } from '@/components/workflows/SelectedGridsProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import { useViewsContext } from '@/components/workflows/ViewsProvider'

export type DocumentFilters = {
  noFlags: boolean
  flagCodes: string[]
  orgIds: string[]
  teamIds: string[]
  tagIds: string[]
} | null

type DocumentFiltersContextValue = {
  documentFilters: DocumentFilters
  isApplyingFilters: boolean
  includeFilter: Record<string, boolean>
  tableColumnFiltersController: ReturnType<typeof useColumnFilters>
  tableCuratedFilters: {
    projectId: string
    workflowStateId?: string
    workflowId?: string
    noFlags?: boolean
    flagCodes?: string[]
    filtering?: DocumentFilteringOption[]
    orgIds?: false | string[]
    teamIds?: string[]
    tagIds?: string[]
  }
  handleOnDocumentFilterChange: (
    documentFilterUpdaterOrValue:
      | DocumentFilters
      | ((val: DocumentFilters) => DocumentFilters),
  ) => void // we use this function when we need to enable the user to save/update view
  resetDocumentFilters: () => void
  setDocumentFilters: Dispatch<SetStateAction<DocumentFilters>>
}

const DocumentFiltersContext = createContext<DocumentFiltersContextValue>(
  {} as DocumentFiltersContextValue,
)

export const useDocumentFiltersContext = () =>
  useContext(DocumentFiltersContext)

type DocumentFiltersProviderProps = {
  children: ReactNode
  isDocumentView: boolean
}

export default function DocumentFiltersProvider({
  children,
  isDocumentView,
}: DocumentFiltersProviderProps) {
  const { pathname, state, search } = useLocation()
  const navigate = useNavigate()
  const { selectedView, handleChange } = useViewsContext()
  const { rootOrganization } = useRootOrganization()
  const [documentFilters, setDocumentFilters] = useState<DocumentFilters>(null)
  const { project } = useProjectContext()
  const baseGrid = getBaseGrid(project.project_grids || [])
  const { selectedGrids, selectedGridFields } = useSelectedGridsContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const tableColumnFiltersController = useColumnFilters({
    fields: selectedGridFields,
    selectedGrids,
    baseGridId: baseGrid?.id,
  })

  const { filteringLogic } = tableColumnFiltersController

  const includeFiltersMap = useMemo(
    () => ({
      noFlags: !!documentFilters?.noFlags,
      flags: !!documentFilters?.flagCodes?.length,
      orgs: !(documentFilters?.orgIds || []).includes(rootOrganization?.id),
      teams: !!documentFilters?.teamIds?.length,
      tags: !!documentFilters?.tagIds?.length,
    }),
    [
      documentFilters?.flagCodes?.length,
      documentFilters?.noFlags,
      documentFilters?.orgIds,
      documentFilters?.tagIds?.length,
      documentFilters?.teamIds?.length,
      rootOrganization?.id,
    ],
  )

  const tableCuratedFilters = useMemo(
    () => ({
      projectId: project.id,
      workflowStateId: selectedWorkflowState?.id,
      workflowId: selectedWorkflow?.id,
      noFlags: includeFiltersMap.noFlags ? documentFilters?.noFlags : undefined,
      flagCodes: includeFiltersMap.flags
        ? documentFilters?.flagCodes
        : undefined,
      filtering: filteringLogic.fieldType
        ? filteringLogic.filtering
        : undefined,
      orgIds: includeFiltersMap.orgs && documentFilters?.orgIds,
      teamIds: includeFiltersMap.teams ? documentFilters?.teamIds : undefined,
      tagIds: includeFiltersMap.tags ? documentFilters?.tagIds : undefined,
    }),
    [
      documentFilters?.flagCodes,
      documentFilters?.noFlags,
      documentFilters?.orgIds,
      documentFilters?.tagIds,
      documentFilters?.teamIds,
      filteringLogic.fieldType,
      filteringLogic.filtering,
      includeFiltersMap.flags,
      includeFiltersMap.noFlags,
      includeFiltersMap.orgs,
      includeFiltersMap.tags,
      includeFiltersMap.teams,
      project.id,
      selectedWorkflow?.id,
      selectedWorkflowState?.id,
    ],
  )

  const isApplyingFilters = useMemo(() => {
    return Boolean(
      tableCuratedFilters.noFlags ||
        tableCuratedFilters.flagCodes ||
        tableCuratedFilters.filtering ||
        tableCuratedFilters.orgIds ||
        tableCuratedFilters.teamIds ||
        tableCuratedFilters.tagIds,
    )
  }, [
    tableCuratedFilters.filtering,
    tableCuratedFilters.flagCodes,
    tableCuratedFilters.noFlags,
    tableCuratedFilters.orgIds,
    tableCuratedFilters.tagIds,
    tableCuratedFilters.teamIds,
  ])

  const handleOnDocumentFilterChange = (
    documentFilterUpdaterOrValue:
      | DocumentFilters
      | ((filters: DocumentFilters) => DocumentFilters),
  ) => {
    handleChange()
    setDocumentFilters(documentFilterUpdaterOrValue)
  }

  const defaultDocumentFilters = useMemo(() => {
    return {
      noFlags: isDocumentView ? selectedView.state.no_flags || false : false,
      flagCodes: selectedView.state.flag_codes || [],
      orgIds: selectedView.state.org_ids?.length
        ? selectedView.state.org_ids
        : [rootOrganization?.id],
      teamIds: selectedView.state.team_ids || [],
      tagIds: selectedView.state.tag_ids || [],
    }
  }, [isDocumentView, rootOrganization?.id, selectedView])

  const resetDocumentFilters = useCallback(() => {
    setDocumentFilters(defaultDocumentFilters)
  }, [defaultDocumentFilters])

  useEffect(() => {
    if (
      rootOrganization?.id &&
      // We don't update filter if we are in settings
      !['settings', 'dashboard'].includes(pathname) &&
      !state
    ) {
      setDocumentFilters((prev) => {
        if (!prev) return defaultDocumentFilters

        if (pathname.includes('documents') || !prev.noFlags) {
          return prev
        }

        return {
          ...prev,
          noFlags: false,
        }
      })
    }
  }, [
    defaultDocumentFilters,
    pathname,
    rootOrganization?.id,
    selectedView.id,
    state,
  ])

  useEffect(() => {
    if (state && 'org_id' in state) {
      const updatedOrgId = state.org_id ? state.org_id : rootOrganization?.id
      setDocumentFilters({ ...defaultDocumentFilters, orgIds: [updatedOrgId] })
      if (
        defaultDocumentFilters.orgIds.length > 1 ||
        defaultDocumentFilters.orgIds[0] !== updatedOrgId
      ) {
        handleChange()
      }

      navigate(`${pathname}${search}`, {
        replace: true,
        // used to prevent resetting of the filters
        state: { reset: false },
      })
    }
  }, [
    defaultDocumentFilters,
    handleChange,
    navigate,
    pathname,
    rootOrganization?.id,
    search,
    state,
  ])

  // Use to clear state when reloading the page
  const handleBeforeUnload = useCallback(() => {
    navigate(`${pathname}${search}`, {
      replace: true,
      state: null,
    })
  }, [navigate, pathname, search])

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleBeforeUnload])

  return (
    <DocumentFiltersContext.Provider
      value={{
        documentFilters,
        isApplyingFilters,
        includeFilter: includeFiltersMap,
        tableColumnFiltersController,
        tableCuratedFilters,
        handleOnDocumentFilterChange,
        resetDocumentFilters,
        setDocumentFilters,
      }}
    >
      {children}
    </DocumentFiltersContext.Provider>
  )
}
